import {
  PublicShareMethod,
  trackPublicShareCompleted,
} from '~publish/helpers/publicTracking'
import { useEffect, useRef, useState } from 'react'
import type { CSSProperties, JSX, ReactNode, Ref } from 'react'

import {
  ArrowLeftIcon,
  Button,
  ChannelIcon,
  CheckIcon,
  CopyIcon,
  Dialog,
  DownloadIcon,
  IconButton,
  Notice,
  Tabs,
  Text,
  Tooltip,
  UnstyledButton,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import type { GuestMedia } from '~publish/helpers/guestMedia'
import { GUEST_NETWORKS } from '~publish/helpers/guestNetworks'
import type { GuestNetwork } from '~publish/helpers/guestNetworks'
import type { GuestMediaAccessController } from '~publish/hooks/useGuestMediaAccess'

import {
  GuestChannelPreview,
  useGuestPreviewImages,
} from './GuestChannelPreview'
import { PublicPostPreview } from './PublicPostPreview'
import { PublicShareNowDeviceShare } from './PublicShareNowDeviceShare'
import {
  getPublicShareUrl,
  PUBLIC_SHARE_DESTINATIONS,
} from './publicShareDestinations'
import type { PublicShareDestination } from './publicShareDestinations'
import { useCopyText, useGuestMediaSaver } from './usePublicShareNowActions'
import type { CopyState } from './usePublicShareNowActions'

import styles from './PublicShareNowHandoff.module.css'

export type PublicShareNowTarget = Readonly<{
  network: GuestNetwork
  destination: PublicShareDestination
}>

type MediaSaver = ReturnType<typeof useGuestMediaSaver>

const PREVIEW_WIDTH = 360

function getOpenDescription(
  destination: PublicShareDestination,
  text: string,
): string {
  return destination.mode === 'intent' && text.length > 0
    ? `${destination.name} opens with your caption ready.`
    : `${destination.name} opens in a new tab, ready for your post.`
}

function getContentDescription(text: string, mediaCount: number): string {
  if (text.length > 0 && mediaCount > 0)
    return 'Copy the text and save the files.'
  if (mediaCount > 0) return 'Save the files.'
  return 'Copy the text.'
}

function NumberedStep({
  index,
  title,
  description,
  children,
}: {
  index: number
  title: string
  description: string
  children: ReactNode
}): JSX.Element {
  return (
    <li className={styles.step}>
      <span className={styles.stepNumber} aria-hidden="true">
        {index}
      </span>
      <div className={styles.stepContent}>
        <div className={styles.stepHeading}>
          <Text as="h4" weight="medium">
            {title}
          </Text>
          <Text as="p" color="subtle">
            {description}
          </Text>
        </div>
        {children}
      </div>
    </li>
  )
}

function ContentCard({
  label,
  action,
  children,
}: {
  label: string
  action: ReactNode
  children: ReactNode
}): JSX.Element {
  return (
    <section className={styles.contentCard} aria-label={label}>
      <Text as="span" weight="medium">
        {label}
      </Text>
      {children}
      <span className={styles.contentCardAction}>{action}</span>
    </section>
  )
}

function MediaTile({
  asset,
  index,
  mediaAccess,
  saver,
}: {
  asset: GuestMedia
  index: number
  mediaAccess: GuestMediaAccessController
  saver: MediaSaver
}): JSX.Element {
  const access = mediaAccess.states.get(asset.assetUrl)
  const status = saver.statuses.get(asset.id)
  const isSaved = status === 'saved'

  return (
    <li className={styles.mediaTile} data-saved={isSaved}>
      {access?.status === 'ready' ? (
        <img
          className={styles.mediaImage}
          src={access.accessUrl}
          alt={asset.altText ?? `Image ${index + 1}`}
        />
      ) : null}
      <IconButton
        variant="secondary"
        size="small"
        label={
          isSaved
            ? `Image ${index + 1} saved`
            : `Save image ${index + 1}, ${asset.fileName}`
        }
        tooltip={isSaved ? 'Saved' : 'Save'}
        className={styles.mediaSave}
        disabled={status === 'saving'}
        onClick={(): void => {
          void saver.save(asset)
        }}
      >
        {isSaved ? <CheckIcon /> : <DownloadIcon />}
      </IconButton>
    </li>
  )
}

function ShareSteps({
  destination,
  text,
  media,
  mediaAccess,
  copy,
  saver,
}: {
  destination: PublicShareDestination
  text: string
  media: readonly GuestMedia[]
  mediaAccess: GuestMediaAccessController
  copy: { state: CopyState; copy: () => Promise<void> }
  saver: MediaSaver
}): JSX.Element {
  if (destination.requiresImage && media.length === 0) {
    return (
      <Notice variant="warning" className={styles.imageNotice}>
        <Notice.Heading>{destination.name} needs an image</Notice.Heading>
        <Notice.Text>
          Add an image to your post to share it on {destination.name}.
        </Notice.Text>
      </Notice>
    )
  }

  const trimmedText = text.trim()

  return (
    <div className={styles.errand}>
      <ol className={styles.steps}>
        <NumberedStep
          index={1}
          title={`Open ${destination.name}`}
          description={getOpenDescription(destination, trimmedText)}
        >
          <div className={styles.stepAction}>
            <Button
              as="a"
              href={getPublicShareUrl(destination, text)}
              onClick={(): void =>
                trackPublicShareCompleted(
                  PublicShareMethod.OPEN_NETWORK,
                  media.length > 0,
                  destination.id,
                )
              }
              target="_blank"
              rel="noopener noreferrer"
              size="small"
            >
              Open {destination.name}
            </Button>
          </div>
        </NumberedStep>
        <NumberedStep
          index={2}
          title="Add the content"
          description={getContentDescription(trimmedText, media.length)}
        >
          <div className={styles.cardStack}>
            <ContentCard
              label="Caption"
              action={
                <IconButton
                  variant="tertiary"
                  size="small"
                  label={
                    copy.state === 'copied' ? 'Caption copied' : 'Copy caption'
                  }
                  tooltip={copy.state === 'copied' ? 'Copied' : 'Copy'}
                  disabled={trimmedText.length === 0}
                  onClick={(): void => {
                    void copy.copy()
                  }}
                >
                  {copy.state === 'copied' ? <CheckIcon /> : <CopyIcon />}
                </IconButton>
              }
            >
              <Text
                as="p"
                color="subtle"
                className={
                  copy.state === 'error' ? styles.cardTextFull : styles.cardText
                }
              >
                {trimmedText.length > 0 ? text : 'No caption for this post.'}
              </Text>
            </ContentCard>
            {media.length > 0 ? (
              <ContentCard
                label="Media"
                action={
                  <IconButton
                    variant="tertiary"
                    size="small"
                    label={
                      saver.allSaved ? 'All images saved' : 'Save all images'
                    }
                    tooltip={saver.allSaved ? 'All saved' : 'Save all'}
                    disabled={saver.isSavingAll}
                    onClick={(): void => {
                      void saver.saveAll()
                    }}
                  >
                    {saver.allSaved ? <CheckIcon /> : <DownloadIcon />}
                  </IconButton>
                }
              >
                <ul className={styles.mediaRow}>
                  {media.map((asset, index) => (
                    <MediaTile
                      key={asset.id}
                      asset={asset}
                      index={index}
                      mediaAccess={mediaAccess}
                      saver={saver}
                    />
                  ))}
                </ul>
              </ContentCard>
            ) : null}
          </div>
        </NumberedStep>
      </ol>
      {copy.state === 'error' ? (
        <Notice variant="error">
          <Notice.Text>
            The caption could not be copied. Select the caption above and copy
            it before you continue.
          </Notice.Text>
        </Notice>
      ) : null}
      {saver.failedCount > 0 ? (
        <Notice variant="error">
          <Notice.Text>
            {saver.failedCount === 1
              ? 'An image could not be saved.'
              : `${saver.failedCount} images could not be saved.`}
          </Notice.Text>
          <Notice.Actions>
            <Button
              size="small"
              onClick={(): void => {
                void saver.saveAll()
              }}
            >
              Try Again
            </Button>
          </Notice.Actions>
        </Notice>
      ) : null}
    </div>
  )
}

function FittedPreview({ children }: { children: ReactNode }): JSX.Element {
  const frameRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const [fit, setFit] = useState({ scale: 1, zoom: 1 })

  useEffect(() => {
    const frame = frameRef.current
    const card = cardRef.current
    if (!frame || !card || typeof ResizeObserver === 'undefined') return

    const measure = (): void => {
      const availableWidth = frame.clientWidth
      const availableHeight = frame.clientHeight
      const naturalHeight = card.offsetHeight
      if (!availableWidth || !naturalHeight) return
      const zoom = Math.min(1, availableWidth / PREVIEW_WIDTH)
      const scale = Math.min(zoom, availableHeight / naturalHeight)
      setFit((current) =>
        current.scale === scale && current.zoom === zoom
          ? current
          : { scale, zoom },
      )
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(frame)
    observer.observe(card)
    return (): void => observer.disconnect()
  }, [])

  return (
    <div ref={frameRef} className={styles.previewFrame}>
      <div
        ref={cardRef}
        className={styles.previewCard}
        style={
          {
            '--preview-width': `${PREVIEW_WIDTH}px`,
            '--preview-scale': fit.scale,
            '--preview-zoom': fit.zoom,
          } as CSSProperties
        }
      >
        {children}
      </div>
    </div>
  )
}

function DestinationList({
  onSelect,
}: {
  onSelect: (destination: PublicShareDestination) => void
}): JSX.Element {
  return (
    <ul className={styles.destinationList}>
      {PUBLIC_SHARE_DESTINATIONS.map((destination) => (
        <li key={destination.id}>
          <UnstyledButton
            type="button"
            className={styles.destinationButton}
            onClick={(): void => onSelect(destination)}
          >
            <ChannelIcon
              type={destination.icon}
              color="branded"
              size="medium"
              className={styles.destinationIcon}
            />
            <span className={styles.destinationName}>{destination.name}</span>
          </UnstyledButton>
        </li>
      ))}
    </ul>
  )
}

export function PublicShareNowHandoff({
  titleRef,
  text,
  media,
  mediaAccess,
  targets,
  onBack,
}: {
  titleRef: Ref<HTMLHeadingElement>
  text: string
  media: readonly GuestMedia[]
  mediaAccess: GuestMediaAccessController
  targets: readonly PublicShareNowTarget[]
  onBack: () => void
}): JSX.Element {
  const [activeNetworkId, setActiveNetworkId] = useState<string>()
  const [pickedDestination, setPickedDestination] =
    useState<PublicShareDestination>()
  const copy = useCopyText(text, media.length > 0)
  const saver = useGuestMediaSaver(media)
  const { images, unavailableCount } = useGuestPreviewImages(media, mediaAccess)
  const activeTarget =
    targets.find((target) => target.network.id === activeNetworkId) ??
    targets[0]
  const genericPreview = (
    <PublicPostPreview text={text} media={media} mediaAccess={mediaAccess} />
  )

  const header = (
    <div className={styles.titleRow}>
      <IconButton
        variant="tertiary"
        size="small"
        label="Back to the choice"
        onClick={onBack}
      >
        <ArrowLeftIcon />
      </IconButton>
      <Dialog.Title ref={titleRef} tabIndex={-1} className={styles.title}>
        Share it manually
      </Dialog.Title>
    </div>
  )

  const renderSteps = (destination: PublicShareDestination): JSX.Element => (
    <ShareSteps
      destination={destination}
      text={text}
      media={media}
      mediaAccess={mediaAccess}
      copy={copy}
      saver={saver}
    />
  )

  const unavailableNote =
    unavailableCount > 0 ? (
      <Text as="p" size="sm" color="subtle" className={styles.previewNote}>
        {unavailableCount === 1
          ? '1 image is unavailable and isn’t shown in this preview.'
          : `${unavailableCount} images are unavailable and aren’t shown in this preview.`}
      </Text>
    ) : null

  const renderNetworkPreview = (network: GuestNetwork): JSX.Element => (
    <section
      className={styles.previewPane}
      aria-label={`Your post on ${network.name}`}
    >
      {unavailableNote}
      <FittedPreview key={network.id}>
        <GuestChannelPreview
          network={network}
          text={text}
          images={images}
          fallback={genericPreview}
        />
      </FittedPreview>
    </section>
  )

  if (!activeTarget) {
    const pickedNetwork = GUEST_NETWORKS.find(
      (network) => network.id === pickedDestination?.id,
    )
    return (
      <div className={styles.panes}>
        <div className={styles.stepsPane}>
          {header}
          <div className={styles.stepsScroll}>
            {pickedDestination ? (
              <>
                <Button
                  variant="tertiary"
                  size="small"
                  className={styles.backToNetworks}
                  onClick={(): void => setPickedDestination(undefined)}
                >
                  <ArrowLeftIcon />
                  Back to networks
                </Button>
                {renderSteps(pickedDestination)}
              </>
            ) : (
              <>
                <Text as="p" color="subtle">
                  Choose where to share your post.
                </Text>
                <DestinationList onSelect={setPickedDestination} />
                <PublicShareNowDeviceShare text={text} media={media} />
              </>
            )}
          </div>
        </div>
        {pickedNetwork ? (
          renderNetworkPreview(pickedNetwork)
        ) : (
          <section className={styles.previewPane} aria-label="Your post">
            <FittedPreview>{genericPreview}</FittedPreview>
          </section>
        )}
      </div>
    )
  }

  const hasTabs = targets.length > 1
  const panes = (
    <>
      <div className={styles.stepsPane}>
        {header}
        {hasTabs ? (
          <Tabs.TabList className={styles.tabList}>
            {targets.map((target) => (
              <Tabs.Tab
                key={target.network.id}
                value={target.network.id}
                className={styles.tab}
              >
                <Tooltip content={target.network.name}>
                  <span className={styles.tabMark}>
                    <ChannelIcon
                      type={target.network.icon}
                      color="subtle"
                      size={20}
                      className={styles.tabIcon}
                    />
                  </span>
                </Tooltip>
                <VisuallyHidden>{target.network.name}</VisuallyHidden>
              </Tabs.Tab>
            ))}
          </Tabs.TabList>
        ) : null}
        <div className={styles.stepsScroll}>
          {hasTabs
            ? targets.map((target) => (
                <Tabs.Panel
                  key={target.network.id}
                  value={target.network.id}
                  className={styles.tabPanel}
                >
                  {renderSteps(target.destination)}
                </Tabs.Panel>
              ))
            : renderSteps(activeTarget.destination)}
        </div>
      </div>
      {renderNetworkPreview(activeTarget.network)}
    </>
  )

  if (!hasTabs) return <div className={styles.panes}>{panes}</div>

  return (
    <Tabs
      value={activeTarget.network.id}
      onChange={setActiveNetworkId}
      className={styles.panes}
    >
      {panes}
    </Tabs>
  )
}
